<!-- OrderHistory.svelte -->

<script>
    import { onMount, onDestroy, createEventDispatcher } from 'svelte';
    import moment from 'moment';

    import { App } from '@capacitor/app';
    import { Capacitor } from '@capacitor/core';
    import backarrow from './assets/backarrow.png'; 
    import OrderDetails from './OrderDetails.svelte'; 

    // Note: 'Plugins' is deprecated. Consider updating to the direct import if you upgrade Capacitor.
    // e.g., import { App } from '@capacitor/app';
    import { Plugins } from '@capacitor/core';
	
    const dispatch = createEventDispatcher();

    let orders = [];
    let currentPage = 1;
    let isLoading = false;
    let canFetchMore = true;
    let isInitialLoading = true;
    let activeTab = 'orders';
    let headerRef;

    let selectedOrder = null;
	let scrollPosition;

    async function fetchOrders(page = 1) {
        if (page === 1) isInitialLoading = true;
        isLoading = true;

        try {
            const accessToken = localStorage.getItem('accessToken');
            const url = `https://groupfruits.online/shop/api/OrderHistoryView/?page=${page}`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `JWT ${accessToken}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                orders = page === 1 ? data.results : [...orders, ...data.results];
                currentPage = page;
                canFetchMore = data.next !== null;
            } else {
                console.error('Error fetching orders: ', response.statusText);
            }
        } catch (error) {
            console.error('Network error: ', error);
        } finally {
            isLoading = false;
            if (page === 1) isInitialLoading = false;
        }
    }

    onMount(() => {
        window.scrollTo(0, 0);
        fetchOrders();

        const backButtonListener = Capacitor.isNativePlatform() ? App.addListener('backButton', goBack) : null;

        return () => {
            if (backButtonListener) {
                backButtonListener.remove();
            }
        };
    });

    onDestroy(() => {
      // This is generally handled by the onMount return function for component-specific listeners.
      // Resetting scroll position might not be desired if navigating away and back quickly.
      window.scrollTo(0, 0);
    });

	function handleOrderClick(order) {
	  scrollPosition = window.pageYOffset;
	  selectedOrder = order;
	}

    async function loadMore() {
        if (canFetchMore && !isLoading) {
            await fetchOrders(currentPage + 1);
        }
    }

    function goBack() {
        dispatch('goToHome');
    }

    function getStatusColor(status) {
        switch (status) {
            case 'Confirmed': return '#4CAF50';
            case 'failed': return '#F44336';
            case 'Packing': return '#2196F3';
            case 'Packed': return '#FF9800';
            case 'Vehicle Started': return '#9C27B0';
            case 'Ready To Pickup': return '#00BCD4';
            case 'Ready To Pickup.': return '#00BCD4';
            case 'Collected': return '#8BC34A'; // Same color, still visible but less prominent due to opacity.
            case 'Cancelled': return '#795548';
            case 'cancelled': return '#795548';
            default: return '#757575';
        }
    }

    function getStatusIcon(status) {
        switch (status) {
            case 'Confirmed': return '✔';
            case 'failed': return '❌';
            case 'Packing': return '👨‍🍳';
            case 'Packed': return '📦';
            case 'Vehicle Started': return '🚚';
            case 'Ready To Pickup': return '🏃';
            case 'Ready To Pickup.': return '🏃';
            case 'Collected': return '✨';
            case 'Cancelled': return '🚫';
            case 'cancelled': return '🚫';
            default: return '❓';
        }
    }
</script>

<div class="order-history">
    <div class="header" bind:this={headerRef}> 
        <span class="back-button" on:click={goBack}>
            <img src={backarrow} alt="Back" /> 
        </span>
        <h1 style="margin-left: 41px;">Order History</h1> 
    </div>

    {#if isInitialLoading}
        <div class="loader-container">
            <div class="loader">
                <svg class="circular" viewBox="25 25 50 50">
                    <circle class="path" cx="50" cy="50" r="20" fill="none" stroke-width="2" stroke-miterlimit="10"/>
                </svg>
            </div>
            <p class="loading-text">Loading your orders...</p>
        </div>
    {:else if orders.length > 0}
		
		<ul class="order-list">
			{#each orders as order (order.id)}
				<!-- 
                    FIX: Add a 'collected' class if the order status is 'Collected'.
                    This class will be used to style the card differently.
                -->
				<li class="order-item" class:collected={order.merged_status === 'Collected'} on:click={() => handleOrderClick(order.id)}>
					
                    <!-- 
                        IMPROVEMENT: Add a visual overlay with a tick mark for collected orders.
                    -->
                    {#if order.merged_status === 'Collected'}
                    <div class="collected-overlay">
                        <span class="tick-icon">✓</span>
                    </div>
                    {/if}

					<div class="status-tag" style="background-color: {getStatusColor(order.merged_status)}">
						<!-- 
                            FIX: Only show pickup code if it exists AND the order is NOT collected.
                            This prevents users from showing old codes.
                        -->
						{#if order.pickup_code && order.merged_status !== 'Collected'}
							<span class="pickup-code">Pickup Code: {order.pickup_code}</span>
						{/if}
						<span class="merged-status">
							{getStatusIcon(order.merged_status)} {order.merged_status}
						</span>
					</div>

                    <!-- FIXED: Ready for pickup notification banner with proper positioning -->
                    {#if order.merged_status === 'Ready To Pickup'}
                    <div class="pickup-ready-banner">
                        <div class="banner-content">
                            <span class="banner-icon">🏃</span>
                            <span class="banner-text">మీ ఆర్డర్ ఇప్పుడు పికప్ తీసుకోవచ్చు</span>
                        </div>
                    </div>
                    {/if}

                    <!-- ADDED: Banner for "Ready To Pickup" status -->
                    {#if order.merged_status === 'Ready To Pickup.'}
                    <div class="pickup-ready-banner">
                        <div class="banner-content">
                            <span class="banner-icon">📦</span>
                            <span class="banner-text">మీ ఆర్డర్ ప్యాక్ చేసి రెడీ గా ఉంది, ఇప్పుడు పికప్ తీసుకోవచ్చు</span>
                        </div>
                    </div>
                    {/if}

                    <div class="order-header">
                        <h2 class="category-title">{order.category_title}</h2>
                        <span class="total-price">₹{Math.round(order.total_price)}</span>
                    </div>
                    <div class="order-details">
                        <p class="product-titles">{order.order_items_product_titles}</p>
                        <span class="order-date">Order Date: {moment(order.created_at).format('DD-MM-YYYY, hh:mm A')}</span> 
                        <span class="pickup-date">Pickup Slot: {order.delivery_date}, {order.delivery_time}</span> 
                    </div>
				</li>
			{/each}
		</ul>		
		
        {#if isLoading}
            <div class="loading">Loading more...</div>
        {:else if canFetchMore}
            <button class="load-more" on:click={loadMore}>Load More</button>
        {/if}
    {:else}
        <p class="no-orders">No orders found.</p>
    {/if}
</div>

<div class="bottom-nav">
    <div 
        class="nav-item {activeTab === 'home' ? 'active' : ''}"
        on:click={() => {
            activeTab = 'home';
            dispatch('goToHome');
        }}
    >
        <span class="nav-icon">🏠</span>
        <span>Home</span>
    </div>
    <div 
        class="nav-item {activeTab === 'orders' ? 'active' : ''}"
        on:click={() => activeTab = 'orders'}
    >
        <span class="nav-icon">📦</span>
        <span>Order History</span>
    </div>
    <div 
        class="nav-item {activeTab === 'settings' ? 'active' : ''}"
        on:click={() => {
            activeTab = 'settings';
            dispatch('profileView');
        }}
    >
        <span class="nav-icon">☰</span>
        <span>Profile</span>
    </div>
</div>


{#if selectedOrder}
  <div class="modal-overlay" on:click={() => {
    selectedOrder = null;
    setTimeout(() => window.scrollTo(0, scrollPosition), 0);
  }}>
    <div class="modal-content" on:click|stopPropagation>
      <OrderDetails orderId={selectedOrder} on:close={() => {
        selectedOrder = null;
        setTimeout(() => window.scrollTo(0, scrollPosition), 0);
      }} />
    </div>
  </div>
{/if}



<style>
    * {
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
    }

    :global(body) {
        padding-top: env(safe-area-inset-top);
        padding-bottom: env(safe-area-inset-bottom);
        padding-left: env(safe-area-inset-left);
        padding-right: env(safe-area-inset-right);
    }

    .header {
        position: fixed;
        top: env(safe-area-inset-top);
        left: 0;
        right: 0;
        background-color: white;
        padding: 16px;
        z-index: 10;
        box-sizing: border-box;
        border-bottom: 0.1px solid #e5dada; 
        box-shadow: none; 
    }

    .bottom-nav {
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        background-color: #ffffff;
        display: flex;
        justify-content: space-around;
        align-items: center;
        height: 60px;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
        z-index: 1000;
        padding-bottom: max(12px, env(safe-area-inset-bottom));
    }

    .nav-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: #777;
        font-size: 0.8rem;
        transition: color 0.3s ease;
    }

    .nav-item.active {
        color: #DD2E44;
    }

    .nav-icon {
        font-size: 1.5rem;
        margin-bottom: 2px;
    }

    .nav-item.active .nav-icon {
        color: #DD2E44;
    }

    .order-history {
        padding: 20px;
        padding-top: calc(64px + env(safe-area-inset-top));
        padding-bottom: calc(80px + env(safe-area-inset-bottom));
        background-color: #fff;
        min-height: 100vh;
    }

    .order-list {
        list-style: none;
        padding: 0;
        margin: 0;
		margin-top: 20px;
    }

    .back-button {
        position: absolute;
        left: 16px;
        top: 50%;
        transform: translateY(-50%);
        width: 24px; 
        height: 24px; 
    }

    .back-button img {
        width: 100%;
        height: 100%;
        object-fit: contain; 
        cursor: pointer; 
    }

    h1 {
        font-size: 19px;
        margin: 0;
    }

    .order-item {
        background-color: #ffffff;
        border-radius: 10px;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
        margin-bottom: 45px;
        padding: 15px;
        cursor: pointer;
        transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, opacity 0.3s ease;
        border: 1px solid #e0e0e0;
        position: relative;
    }

    .order-item:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        border-color: #d0d0d0;
    }

    .status-tag {
        position: absolute;
        top: -12px;
        padding: 5px 10px;
        border-radius: 5px;
        color: white;
        font-size: 12px;
        display: flex;
        align-items: center;
        white-space: nowrap;
        z-index: 2; /* Ensure status tag is above the overlay */
    }

    .pickup-code {
        margin-right: 10px;
    }

    .pickup-date { 
        font-size: 12px;
        color: #888;
        display: block; 
    }
	
    .merged-status {
        display: flex;
        align-items: center;
    }

    /* FIXED: Pickup ready notification banner styles with relative positioning */
    .pickup-ready-banner {
        /* Changed from absolute to relative positioning */
        position: relative;
        background: linear-gradient(135deg, #4CAF50, #45a049);
        border-radius: 8px;
        padding: 10px 12px;
        margin-top: 0px; /* Small gap from status tag */
        margin-bottom: 5px; /* Gap before order header */
        z-index: 3;
        animation: pulse-glow 2s ease-in-out infinite;
        box-shadow: 0 2px 10px rgba(76, 175, 80, 0.3);
    }

    .banner-content {
        display: flex;
        align-items: center;
        color: white;
        font-size: 13px;
        font-weight: 500;
    }

    .banner-icon {
        margin-right: 8px;
        font-size: 16px;
        animation: bounce 1.5s ease-in-out infinite;
    }

    .banner-text {
        flex: 1;
        line-height: 1.3;
    }

    @keyframes pulse-glow {
        0%, 100% {
            box-shadow: 0 2px 10px rgba(76, 175, 80, 0.3);
        }
        50% {
            box-shadow: 0 2px 20px rgba(76, 175, 80, 0.5);
        }
    }

    @keyframes bounce {
        0%, 100% {
            transform: translateY(0);
        }
        50% {
            transform: translateY(-3px);
        }
    }

    .order-header {
        display: flex;
        flex-direction: column;
        margin-bottom: 10px;
        /* REMOVED: Fixed margin-top since banner now uses relative positioning */
    }

    .category-title {
        font-size: 15px;
        font-weight: 600;
        margin: 10px 0 8px 0;
    }

    .total-price {
        font-size: 15px;
        font-weight: 600;
        color: #231F1F;
        margin-bottom: 6px;
    }

    .order-details {
        border-top: 1px solid #e0e0e0;
        padding-top: 10px;
    }

    .product-titles {
        margin: 0 0 5px 0;
        font-size: 14px;
        color: #555;
    }

    .order-date {
        font-size: 12px;
        color: #888;
    }

    .loading {
        text-align: center;
        padding: 10px;
        color: #666;
    }

    .load-more {
        display: block;
        width: 100%;
        padding: 10px;
        background-color: #4CAF50;
        color: white;
        border: none;
        border-radius: 5px;
        font-size: 16px;
        cursor: pointer;
        transition: background-color 0.2s ease;
		margin-bottom: 80px;
    }

    .load-more:hover {
        background-color: #45a049;
    }

    .no-orders {
        text-align: center;
        color: #666;
        font-style: italic;
    }

    /* --- NEW STYLES FOR COLLECTED ORDERS --- */

    .order-item.collected {
        /* Slightly fade out the card to make it less prominent */
        opacity: 0.8;
    }

    .collected-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1; /* Sit between the card background and its content */
        pointer-events: none; /* Crucial: allows clicks to pass through to the card below */
        overflow: hidden; /* Ensures the icon doesn't bleed outside the card's rounded corners */
        border-radius: 10px; /* Match the parent card's border-radius */
    }

    .tick-icon {
        font-size: 120px;
        font-weight: bold;
        color: rgba(76, 175, 80, 0.2); /* A very transparent, non-intrusive green */
        transform: rotate(-15deg); /* A slight tilt for a more dynamic feel */
        text-shadow: 0px 0px 5px rgba(255, 255, 255, 0.5); /* A soft white glow to help it stand out */
    }

    /* --- END OF NEW STYLES --- */


    .loader-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        height: 100vh;
    }

    .loader {
        position: relative;
        margin: 0 auto;
        width: 100px;
    }

    .loader:before {
        content: '';
        display: block;
        padding-top: 100%;
    }

    .circular {
        animation: rotate 2s linear infinite;
        height: 100%;
        transform-origin: center center;
        width: 100%;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        margin: auto;
    }

    .path {
        stroke-dasharray: 1, 200;
        stroke-dashoffset: 0;
        animation: dash 1.5s ease-in-out infinite, color 6s ease-in-out infinite;
        stroke-linecap: round;
    }

    .loading-text {
        margin-top: 20px;
        font-size: 16px;
        color: #555;
        animation: pulse 1.5s ease-in-out infinite;
    }

    @keyframes rotate { 100% { transform: rotate(360deg); } }
    @keyframes dash { 0% { stroke-dasharray: 1, 200; stroke-dashoffset: 0; } 50% { stroke-dasharray: 89, 200; stroke-dashoffset: -35px; } 100% { stroke-dasharray: 89, 200; stroke-dashoffset: -124px; } }
    @keyframes color { 100%, 0% { stroke: #4CAF50; } 40% { stroke: #2196F3; } 66% { stroke: #FF9800; } 80%, 90% { stroke: #F44336; } }
    @keyframes pulse { 0% { opacity: 0.6; } 50% { opacity: 1; } 100% { opacity: 0.6; } }

    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1000;
    }

    .modal-content {
      background-color: white;
      padding: 20px;
      border-radius: 8px;
      max-width: 90%;
      max-height: 90%;
      overflow-y: auto;
    }
</style>